import { useMemo, useState, type ReactNode } from 'react';
import { EmptyState } from './ui';

/**
 * The list table every admin page uses.
 *
 * Sorting and paging happen in the browser, as the original's DataTables did. That is the right trade
 * here: the server already caps a list at 500 rows, so the whole set is in hand, and doing it locally
 * means sorting a column costs nothing and never shows a spinner.
 *
 * A column is sortable only if it says how to sort itself. Cells render arbitrary markup — an avatar,
 * a pill, a row of buttons — and there is no honest way to derive an ordering from that.
 */
export interface Column<T> {
    header: string;
    cell: (row: T) => ReactNode;
    className?: string;
    /** Supply this to make the column sortable. */
    sortBy?: (row: T) => string | number | null;
}

type Direction = 'asc' | 'desc';

export function DataTable<T>({ rows, columns, empty, rowKey, pageSize = 25, caption }: {
    rows: T[];
    columns: Column<T>[];
    empty: string;
    rowKey: (row: T) => string | number;
    /** 0 turns paging off, for a short list that will never need it. */
    pageSize?: number;
    /** Shown instead of "Showing 1 to 5 of 5 entries" when the count needs different words. */
    caption?: string;
}) {
    const [sort, setSort] = useState<{ header: string; direction: Direction } | null>(null);
    const [page, setPage] = useState(1);

    const sorted = useMemo(() => {
        if (!sort) return rows;

        const column = columns.find((c) => c.header === sort.header);
        if (!column?.sortBy) return rows;

        const factor = sort.direction === 'asc' ? 1 : -1;

        return [...rows].sort((a, b) => {
            const left = column.sortBy!(a);
            const right = column.sortBy!(b);

            // Blanks sink to the bottom whichever way the column is sorted: a candidate with no email
            // is not "before A" or "after Z", they are simply missing.
            if (left === null || left === '') return 1;
            if (right === null || right === '') return -1;

            if (typeof left === 'number' && typeof right === 'number') {
                return (left - right) * factor;
            }

            return String(left).localeCompare(String(right), undefined, { numeric: true }) * factor;
        });
    }, [rows, columns, sort]);

    const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
    const current = Math.min(page, pageCount);

    const visible = pageSize > 0
        ? sorted.slice((current - 1) * pageSize, current * pageSize)
        : sorted;

    if (rows.length === 0) {
        return <EmptyState message={empty} />;
    }

    const toggle = (header: string) => {
        setPage(1);
        setSort((existing) =>
            existing?.header === header
                ? { header, direction: existing.direction === 'asc' ? 'desc' : 'asc' }
                : { header, direction: 'asc' });
    };

    const from = pageSize > 0 ? (current - 1) * pageSize + 1 : 1;
    const to = pageSize > 0 ? Math.min(current * pageSize, sorted.length) : sorted.length;

    return (
        <div>
            <div className="overflow-x-auto">
                <table className="app-table">
                    <thead>
                        <tr>
                            {columns.map((column) => {
                                const active = sort?.header === column.header;

                                return (
                                    <th key={column.header} className={column.className ?? ''}>
                                        {column.sortBy ? (
                                            <button
                                                type="button"
                                                onClick={() => toggle(column.header)}
                                                className="app-sort"
                                                aria-sort={active
                                                    ? (sort!.direction === 'asc' ? 'ascending' : 'descending')
                                                    : 'none'}
                                            >
                                                {column.header}
                                                <i
                                                    className={`bi ${
                                                        active
                                                            ? sort!.direction === 'asc'
                                                                ? 'bi-caret-up-fill'
                                                                : 'bi-caret-down-fill'
                                                            : 'bi-arrow-down-up'
                                                    }`}
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        ) : column.header}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <tr key={rowKey(row)}>
                                {columns.map((column) => (
                                    <td key={column.header} className={column.className ?? ''}>
                                        {column.cell(row)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="app-table-foot">
                <span>
                    {caption ?? `Showing ${from} to ${to} of ${sorted.length} entr${sorted.length === 1 ? 'y' : 'ies'}`}
                    {/* The server caps the list. Saying so beats letting someone believe they are
                        seeing everything there is. */}
                    {rows.length >= 500 && ' — the most recent 500. Use search to narrow this down.'}
                </span>

                {pageCount > 1 && (
                    <nav className="app-pager" aria-label="Pages">
                        <button type="button" onClick={() => setPage(1)} disabled={current === 1} aria-label="First page">«</button>
                        <button type="button" onClick={() => setPage(current - 1)} disabled={current === 1} aria-label="Previous page">‹</button>
                        <span className="is-current">{current}</span>
                        <button type="button" onClick={() => setPage(current + 1)} disabled={current === pageCount} aria-label="Next page">›</button>
                        <button type="button" onClick={() => setPage(pageCount)} disabled={current === pageCount} aria-label="Last page">»</button>
                    </nav>
                )}
            </div>
        </div>
    );
}
